import React, { useState, useEffect } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiSave } = FiIcons;

export default function SnippetForm({ onSave, onCancel, initialData = null }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    code: '',
    language: 'javascript',
    framework: '',
    tags: '',
    additional_instructions: ''
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        title: initialData.title || '',
        description: initialData.description || '',
        code: initialData.code || '',
        language: (initialData.language || 'javascript').toLowerCase(),
        framework: initialData.framework || '',
        tags: Array.isArray(initialData.tags) ? initialData.tags.join(', ') : (initialData.tags || ''),
        additional_instructions: initialData.additional_instructions || ''
      });
    }
  }, [initialData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const snippetData = {
      ...formData,
      id: initialData?.id,
      // Pass tags as array to App.jsx which will join them for the DB
      tags: typeof formData.tags === 'string' 
        ? formData.tags.split(',').map(tag => tag.trim()).filter(Boolean)
        : formData.tags
    };
    onSave(snippetData);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="flex-1 overflow-y-auto bg-slate-950 p-6 custom-scrollbar">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <button 
            onClick={onCancel}
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm font-medium"
          >
            <SafeIcon icon={FiArrowLeft} />
            Cancel
          </button>
          <h2 className="text-xl font-bold text-white">
            {initialData ? 'Edit Snippet' : 'New Snippet'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 pb-12">
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Title</label>
                <input 
                  type="text" 
                  name="title"
                  required
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="e.g., WordPress Custom Post Type Setup"
                  className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-slate-100 focus:outline-none focus:border-indigo-500 transition-colors"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Description</label>
                <textarea 
                  name="description"
                  required
                  value={formData.description}
                  onChange={handleChange}
                  rows="2"
                  placeholder="Briefly explain what this code does..."
                  className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-slate-100 focus:outline-none focus:border-indigo-500 transition-colors resize-none"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Language</label>
                <select 
                  name="language"
                  value={formData.language}
                  onChange={handleChange}
                  className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-slate-100 focus:outline-none focus:border-indigo-500 transition-colors"
                >
                  <option value="javascript">JavaScript</option>
                  <option value="php">PHP</option>
                  <option value="css">CSS / SCSS</option>
                  <option value="html">HTML</option>
                  <option value="python">Python</option>
                  <option value="bash">Bash</option>
                  <option value="json">JSON</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Framework</label>
                <input 
                  type="text" 
                  name="framework"
                  required
                  value={formData.framework}
                  onChange={handleChange}
                  placeholder="e.g., React, WordPress"
                  className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-slate-100 focus:outline-none focus:border-indigo-500 transition-colors"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Tags (Comma separated)</label>
                <input 
                  type="text" 
                  name="tags"
                  value={formData.tags}
                  onChange={handleChange}
                  placeholder="hook, utility, frontend"
                  className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-slate-100 focus:outline-none focus:border-indigo-500 transition-colors"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Additional Instructions (Optional)</label>
                <textarea 
                  name="additional_instructions"
                  value={formData.additional_instructions}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Any extra notes or usage instructions..."
                  className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-slate-100 focus:outline-none focus:border-indigo-500 transition-colors resize-none"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Code</label>
                <textarea 
                  name="code"
                  required
                  value={formData.code}
                  onChange={handleChange}
                  rows="10"
                  placeholder="Paste your code here..."
                  className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-3 text-slate-300 font-mono text-xs focus:outline-none focus:border-indigo-500 transition-colors"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button 
              type="submit"
              className="bg-indigo-600 hover:bg-indigo-700 text-white flex items-center gap-2 py-2 px-6 rounded-lg font-medium transition-colors shadow-lg shadow-indigo-600/20"
            >
              <SafeIcon icon={FiSave} />
              {initialData ? 'Update' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}